import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getLanguageFromPath, addLanguagePrefix } from '@/lib/i18n';

interface LanguageWrapperProps {
  children: React.ReactNode;
}

/**
 * Syncs the URL language prefix with i18n language state.
 * Wraps the app to ensure language is always in sync with the URL.
 * Also handles first-visit auto-redirect for Spanish-speaking users.
 */
const LanguageWrapper = ({ children }: LanguageWrapperProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  useEffect(() => {
    // Check if this is a first visit (no cached language preference)
    const hasVisited = localStorage.getItem('i18nextLng');
    const urlLang = getLanguageFromPath(location.pathname);
    
    // First visit: auto-redirect Spanish-speaking users to /es/ routes
    if (!hasVisited && urlLang === 'en') {
      const browserLang = navigator.language.slice(0, 2).toLowerCase();
      
      if (browserLang === 'es') {
        // Redirect to Spanish version of current page
        const spanishPath = addLanguagePrefix(location.pathname, 'es');
        navigate(spanishPath, { replace: true });
        return;
      }
    }
    
    // Sync URL language with i18n
    if (i18n.language !== urlLang) {
      i18n.changeLanguage(urlLang);
    }
  }, [location.pathname, i18n, navigate]);

  return <>{children}</>;
};

export default LanguageWrapper;
